import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { useOrderStore } from '../stores/order'
import { useCartStore } from '../stores/cart'
import {
  Package,
  MapPin,
  CreditCard,
  Calendar,
  Download,
  RotateCcw,
  CheckCircle2,
  Circle,
  Clock,
  Truck,
  PackageCheck,
  Phone,
} from 'lucide-react'
import { format } from 'date-fns'
import type { OrderStatus } from '../types/order'
import { useState } from 'react'

const STATUS_PROGRESSION: OrderStatus[] = [
  'pending',
  'confirmed',
  'processing',
  'packed',
  'shipped',
  'out_for_delivery',
  'delivered',
]

const STATUS_ICONS: Record<OrderStatus, React.ReactNode> = {
  pending: <Clock className="w-5 h-5" />,
  confirmed: <CheckCircle2 className="w-5 h-5" />,
  processing: <Package className="w-5 h-5" />,
  packed: <PackageCheck className="w-5 h-5" />,
  shipped: <Truck className="w-5 h-5" />,
  out_for_delivery: <Truck className="w-5 h-5" />,
  delivered: <CheckCircle2 className="w-5 h-5" />,
  cancelled: <Circle className="w-5 h-5" />,
  refunded: <Circle className="w-5 h-5" />,
}

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Order Placed',
  confirmed: 'Confirmed',
  processing: 'Processing',
  packed: 'Packed',
  shipped: 'Shipped',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { orders } = useOrderStore()
  const { addItem } = useCartStore()
  const [reordering, setReordering] = useState(false)

  const order = orders.find((o) => o.id === orderId)

  if (!isAuthenticated) {
    return <Navigate to="/login" />
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Order not found</h2>
          <p className="text-gray-600 mb-6">The order you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/orders"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            View All Orders
          </Link>
        </div>
      </div>
    )
  }

  const currentStatusIndex = STATUS_PROGRESSION.indexOf(order.status)
  const isCancelledOrRefunded = order.status === 'cancelled' || order.status === 'refunded'

  const handleReorder = () => {
    setReordering(true)
    try {
      let addedCount = 0
      order.items.forEach((item) => {
        // In a real app, we'd check product availability and current price
        addItem({
          product_id: item.product_id,
          title: item.title,
          image: item.image,
          price: item.price,
          original_price: item.original_price,
          vendor_name: item.vendor_name,
        }, item.quantity)
        addedCount++
      })
      alert(`✓ ${addedCount} items added to cart!\n\nNote: Prices and availability have been updated to current rates.`)
      navigate('/cart')
    } catch (error) {
      alert('Failed to reorder. Please try again.')
    } finally {
      setReordering(false)
    }
  }

  const handleDownloadInvoice = () => {
    // In a real app, this would generate a PDF invoice
    alert(
      `Invoice Download\n\nOrder #${order.order_number}\nTotal: ৳${order.total.toFixed(2)}\n\nIn a production app, a PDF invoice would be downloaded here.`
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <Link to="/orders" className="hover:text-blue-600">
            Orders
          </Link>
          <span>/</span>
          <span>#{order.order_number}</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Order #{order.order_number}</h1>
            <p className="text-gray-600">
              Placed on {format(new Date(order.created_at), 'MMMM dd, yyyy • hh:mm a')}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReorder}
              disabled={reordering}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              {reordering ? 'Reordering...' : 'Reorder'}
            </button>
            {order.status === 'delivered' && (
              <button
                onClick={handleDownloadInvoice}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Invoice
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Timeline */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Order Status</h2>

            {isCancelledOrRefunded ? (
              <div className="space-y-4">
                {order.timeline.map((event, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          event.status === 'cancelled' || event.status === 'refunded'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {STATUS_ICONS[event.status]}
                      </div>
                      {index < order.timeline.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" />}
                    </div>
                    <div className="flex-1 pb-6">
                      <h3 className="font-semibold">{STATUS_LABELS[event.status]}</h3>
                      <p className="text-sm text-gray-600">{event.note}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {format(new Date(event.timestamp), 'MMM dd, yyyy • hh:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {STATUS_PROGRESSION.map((status, index) => {
                  const isCompleted = index <= currentStatusIndex
                  const event = order.timeline.find((e) => e.status === status)

                  return (
                    <div key={status} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                            isCompleted
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                        </div>
                        {index < STATUS_PROGRESSION.length - 1 && (
                          <div
                            className={`w-0.5 h-12 my-1 transition-colors ${
                              isCompleted ? 'bg-green-200' : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <h3 className={`font-semibold ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                          {STATUS_LABELS[status]}
                        </h3>
                        {event && (
                          <>
                            <p className="text-sm text-gray-600">{event.note}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {format(new Date(event.timestamp), 'MMM dd, yyyy • hh:mm a')}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Order Items ({order.items.length})</h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-200 last:border-0">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{item.vendor_name}</p>
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                      <p className="text-sm font-medium">৳{item.price.toFixed(2)} each</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">৳{item.subtotal.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span>৳{order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Delivery Fee</span>
                <span>
                  {order.shipping_cost === 0 ? (
                    <span className="text-green-600 font-medium">FREE</span>
                  ) : (
                    `৳${order.shipping_cost.toFixed(2)}`
                  )}
                </span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Discount {order.coupon_code && `(${order.coupon_code})`}
                  </span>
                  <span className="text-green-600">-৳{order.discount.toFixed(2)}</span>
                </div>
              )}
              {order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span>৳{order.tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>৳{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Delivery Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Delivery Address
            </h2>
            <div className="space-y-2 text-sm">
              <p className="font-medium">{order.delivery_address.label}</p>
              <p className="text-gray-600">{order.delivery_address.address_line1}</p>
              {order.delivery_address.address_line2 && (
                <p className="text-gray-600">{order.delivery_address.address_line2}</p>
              )}
              <p className="text-gray-600">
                {order.delivery_address.area}, {order.delivery_address.city}
              </p>
              <p className="text-gray-600">{order.delivery_address.postal_code}</p>
              <div className="flex items-center gap-2 text-gray-600 pt-2">
                <Phone className="w-4 h-4" />
                <span>{order.delivery_address.phone}</span>
              </div>
            </div>
          </div>

          {/* Delivery Slot */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Delivery Slot
            </h2>
            <div className="space-y-2 text-sm">
              <p className="font-medium">{format(new Date(order.delivery_slot.date), 'EEEE, MMMM dd, yyyy')}</p>
              <p className="text-gray-600">{order.delivery_slot.label}</p>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment
            </h2>
            <div className="space-y-2 text-sm">
              <p className="font-medium">{order.payment_method}</p>
              <p className="text-gray-600">
                Status:{' '}
                <span
                  className={
                    order.payment_status === 'paid'
                      ? 'text-green-600 font-medium'
                      : order.payment_status === 'failed'
                        ? 'text-red-600 font-medium'
                        : order.payment_status === 'refunded'
                          ? 'text-orange-600 font-medium'
                          : 'text-yellow-600 font-medium'
                  }
                >
                  {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                </span>
              </p>
            </div>
          </div>

          {/* Order Notes */}
          {order.notes && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Order Notes</h2>
              <p className="text-sm text-gray-600">{order.notes}</p>
            </div>
          )}

          {/* Need Help */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Need Help?</h2>
            <p className="text-sm text-gray-600 mb-4">Contact our customer support for assistance</p>
            <Link
              to="/support"
              className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
